import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

export interface PendingSectionSave {
  /** The section holds changes its own API has not received yet. */
  isDirty: boolean;
  save: () => Promise<void>;
}

export interface PendingSectionSavesRegistry {
  register: (key: string, entry: PendingSectionSave) => void;
  unregister: (key: string) => void;
  /** Saves every dirty section through its own API; each save reports its own result. */
  flushAll: () => Promise<void>;
}

const noop = () => undefined;

const PendingSectionSavesContext = createContext<PendingSectionSavesRegistry>({
  register: noop,
  unregister: noop,
  flushAll: () => Promise.resolve(),
});

export interface PendingSectionSavesProviderProps {
  children: ReactNode;
}

/**
 * Sections that persist through their own API (social history, ROS/PE, screeners,
 * chart lists) register here so Save & Close and Save & Sign can flush them with the note.
 */
export const PendingSectionSavesProvider = ({
  children,
}: PendingSectionSavesProviderProps) => {
  const entries = useRef(new Map<string, PendingSectionSave>());
  const register = useCallback(
    (key: string, entry: PendingSectionSave) => entries.current.set(key, entry),
    []
  );
  const unregister = useCallback(
    (key: string) => entries.current.delete(key),
    []
  );
  const flushAll = useCallback(async () => {
    const dirty = [...entries.current.values()].filter((it) => it.isDirty);
    await Promise.all(dirty.map((it) => it.save().catch(noop)));
  }, []);
  const value = useMemo(
    () => ({ register, unregister, flushAll }),
    [register, unregister, flushAll]
  );
  return (
    <PendingSectionSavesContext.Provider value={value}>
      {children}
    </PendingSectionSavesContext.Provider>
  );
};

export const usePendingSectionSaves = (): PendingSectionSavesRegistry =>
  useContext(PendingSectionSavesContext);

/** Keeps a section's dirty flag and save callback current in the registry while it is mounted. */
export const useRegisterSectionSave = (
  key: string,
  isDirty: boolean,
  save: () => Promise<void>
): void => {
  const { register, unregister } = usePendingSectionSaves();
  useEffect(() => {
    register(key, { isDirty, save });
    return () => unregister(key);
  }, [key, isDirty, save, register, unregister]);
};
